// Async data loading hook (Task 13.7).
//
// Drives the loading -> success | error lifecycle for a single dashboard
// section or view. A request that does not resolve within 30 seconds is
// treated as failed (Req 29.6). Each section uses its own instance so a
// failure in one section never affects another (Req 20.9, 21.7). `reload`
// backs the retry control (Req 29.5).

import { useCallback, useEffect, useRef, useState } from "react";

/** Requests that do not resolve within this many ms are treated as failed. */
export const REQUEST_TIMEOUT_MS = 30_000;

export type AsyncState<T> =
  | { status: "loading" }
  | { status: "error"; error: Error; data?: T }
  | { status: "success"; data: T };

class TimeoutError extends Error {
  constructor() {
    super("The request timed out.");
    this.name = "TimeoutError";
  }
}

/**
 * Load data from `loader`, exposing an {@link AsyncState} and a `reload`
 * function. A 30-second watchdog rejects a stalled request (Req 29.6).
 * Previously loaded data is preserved on the error state (Req 29.5).
 *
 * @param loader      Async producer of the section's data.
 * @param timeoutMs   Override the request timeout (tests use a short value).
 */
export function useAsyncData<T>(
  loader: () => Promise<T>,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
): { state: AsyncState<T>; reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ status: "loading" });
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const mountedRef = useRef(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setState({ status: "loading" });
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new TimeoutError()), timeoutMs);
    });

    setState((prev) =>
      prev.status === "loading" ? prev : { status: "loading" },
    );

    Promise.race([loaderRef.current(), timeout])
      .then((data) => {
        if (mountedRef.current) setState({ status: "success", data: data as T });
      })
      .catch((err: unknown) => {
        if (mountedRef.current) {
          setState({
            status: "error",
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      })
      .finally(() => clearTimeout(timer));

    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, timeoutMs]);

  return { state, reload };
}
